import React, { useState, useRef, useEffect } from 'react';
import { preventPropagation } from '../../utils/event-utils';
import { isBreakPointMobile, isMobileOrTabletDevice } from '../../utils/media-utils';
import { usePlayerContext } from '../../context/player-context';

// Import sub-components
import { ProgressBar } from '../controls/progress-bar';
import { PlaybackControls } from '../controls/playback-controls';
import { VolumeControl } from '../controls/volume-control';
import { SettingsControl } from '../controls/settings-control';
import { CastToggleButton } from '../features/cast-button';
import { AirPlayButton } from '../features/airplay-button';
import { SubtitlesButton } from '../features/subtitles-button';

export interface PlayerControlsProps {
  autoPlayFailed: boolean;
  onTimelineHover?: (e: React.MouseEvent<HTMLDivElement>) => void;
  muxPlayerRef: React.MutableRefObject<any>;
}

export const PlayerControls: React.FC<PlayerControlsProps> = ({
  onTimelineHover,
  autoPlayFailed,
  muxPlayerRef,
}) => {
  const {
    isPlaying,
    currentTime,
    duration,
    showControls: shouldShowControls,
    playbackSpeed,
    volume,
    isFullscreen,
    loopEnabled,
    formatTime,
    storyboard,
    handleTimelineHover,
    simpleControls,

    // Actions
    togglePlayPause,
    handleRewind,
    handleForward,
    handleSeek,
    handleVolumeChange,
    toggleMute,
    handlePlaybackSpeedChange,
    handleFullscreen,
    openLoopModal,
    clearLoop,
    setMouseOverControls,
  } = usePlayerContext();

  const [isMobile, setIsMobile] = useState(false);
  const controlsContainerRef = useRef<HTMLDivElement>(null);

  // Add this function to handle mouse movement over the progress bar
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (storyboard) {
      handleTimelineHover(e, 'progress-bar-container');
    }
    if (onTimelineHover) {
      onTimelineHover(e);
    }
  };

  // Check device on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(isBreakPointMobile());
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    // Remove the console.log('[PlayerControls] showControls changed:', shouldShowControls);
  }, [shouldShowControls]);

  return (
    <div
      ref={controlsContainerRef}
      className={`absolute bottom-0 left-0 right-0 z-30 w-full controls-container`}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        background: 'rgba(32,33,39,0.85)',
        boxShadow: shouldShowControls ? '0 -4px 6px rgba(0, 0, 0, 0.1)' : 'none',
        transition: 'opacity 0.5s ease, transform 0.5s ease, box-shadow 0.5s ease',
        opacity: shouldShowControls ? 1 : 0,
        transform: shouldShowControls ? 'translateY(0)' : 'translateY(100%)',
        pointerEvents: shouldShowControls ? 'auto' : 'none',
      }}
      onMouseEnter={() => {
        setMouseOverControls(true);
      }}
      onMouseLeave={() => {
        setMouseOverControls(false);
      }}
      onMouseMove={handleMouseMove}
      onClick={preventPropagation}
    >
      <ProgressBar
        id="progress-bar-container"
        currentTime={currentTime}
        duration={duration}
        onSeek={handleSeek}
      />

      <div className="flex items-center justify-between p-2 md:px-4 md:py-1 h-[56px] w-full">
        {/* Left controls */}
        <div className="flex items-center">
          {simpleControls ? (
            <button onClick={togglePlayPause} className="p-2 text-white hover:text-primary-light">
              {isPlaying ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          ) : (
            <PlaybackControls
              isPlaying={isPlaying}
              togglePlayPause={togglePlayPause}
              handleRewind={handleRewind}
              handleForward={handleForward}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Center timestamp */}
        <div className="flex-1 text-xs font-medium text-center text-white md:text-sm">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Right controls */}
        <div className="flex items-center space-x-1 md:space-x-2 justify-end">
          {/* Only show volume control for non-mobile */}
          {!isMobileOrTabletDevice() && (
            <VolumeControl
              volume={volume}
              onVolumeChange={handleVolumeChange}
              toggleMute={toggleMute}
            />
          )}
          {muxPlayerRef && muxPlayerRef?.current && (
            <>
              <SubtitlesButton muxPlayerRef={muxPlayerRef} />
              <AirPlayButton muxPlayerRef={muxPlayerRef} />
              <CastToggleButton muxPlayerRef={muxPlayerRef} />
            </>
          )}
          {simpleControls ? (
            // Simple controls - just fullscreen button
            <button onClick={handleFullscreen} className="p-2 text-white hover:text-primary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
              </svg>
            </button>
          ) : (
            // Original settings control
            <SettingsControl
              isMobile={isMobile}
              playbackSpeed={playbackSpeed}
              isFullscreen={isFullscreen}
              loopEnabled={loopEnabled}
              handlePlaybackSpeedChange={handlePlaybackSpeedChange}
              handleFullscreen={handleFullscreen}
              openLoopModal={openLoopModal}
              clearLoop={clearLoop}
              toggleMute={toggleMute}
              volume={volume}
            />
          )}
        </div>
      </div>
    </div>
  );
};
